import { useApolloClient, useQuery } from '@apollo/client';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { PageNotFound } from '@openx/components-core/lib/PageNotFound/PageNotFound';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions';
import { PageHeader } from '@openx/components-header/PageHeader';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { GetBidderCreativeDocument } from '../../graphql/query/creatives/GetBidderCreative.generated';
// import { Tab, Tabs } from '@mui/material';
import { CreativeDetailsTab } from './CreativeDetails/CreativeDetailsTab';
import { useDeleteCreative } from './CreativesList/hooks/useDeleteCreative';
import { useFetchRequiredOptions } from './hooks/useFetchRequiredOptions';

const creativeTabs = {
  DETAILS: 'details',
} as const;

// type CreativeTab = (typeof creativeTabs)[keyof typeof creativeTabs];

export const CreativeDetailsPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { creativeId = '' } = useParams<{ creativeId: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { openDeleteModal } = useDeleteCreative();

  const {
    data: creativeData,
    loading: creativeLoading,
    error: creativeError,
  } = useQuery(GetBidderCreativeDocument, {
    variables: { id: creativeId },
  });

  const {
    advertiserOptions,
    iabCategoryOptions,
    isLoading: optionsLoading,
  } = useFetchRequiredOptions({
    includeIabCategories: true,
  });

  if (creativeLoading || optionsLoading) {
    return (
      <PageHeader title={t('Creative Details')} titlePrefix="OpenXSelect" goBack={() => navigate('/aqua/creatives')}>
        <Loader />
      </PageHeader>
    );
  }

  if (creativeError || !creativeData?.bidderCreativeById) {
    enqueueSnackbar(t('Failed to load creative'), { variant: 'error' });

    return <PageNotFound appName="OpenXSelect" />;
  }

  const creative = creativeData.bidderCreativeById;

  // const handleChangeTab = (_: React.SyntheticEvent, tab: CreativeTab) => {
  //   navigate(`/creatives/${creativeId}/${tab}`);
  // };

  return (
    <PageHeader
      title={t('Details: {name}', { name: creative.name })}
      titlePrefix="OpenXSelect"
      goBack={() => navigate('/aqua/creatives')}
      render={() => (
        <HeaderActions
          actions={[
            {
              color: 'secondary',
              'data-test': 'edit-action-button',
              isAllowed: true,
              label: t('Edit'),
              onClick: () => navigate(`/aqua/creatives/${creativeId}/edit`),
              showAsActionButton: false,
            },
            {
              color: 'error',
              'data-test': 'delete-action-button',
              isAllowed: true,
              label: t('Delete'),
              onClick: () =>
                openDeleteModal({
                  creative,
                  onSuccess: () => {
                    apolloClient.cache.evict({ id: `BidderCreative:${creative.id}` });
                    apolloClient.cache.gc();
                    navigate('/aqua/creatives');
                  },
                }),
              showAsActionButton: false,
            },
          ]}
        />
      )}
    >
      {/* <Tabs onChange={handleChangeTab} value={creativeTabs.DETAILS}>
        <Tab data-test="details-tab" label={t('Details')} value={creativeTabs.DETAILS} />
      </Tabs> */}

      <Routes>
        <Route
          path={creativeTabs.DETAILS}
          element={
            <CreativeDetailsTab
              creative={creative}
              advertiserOptions={advertiserOptions}
              iabCategoryOptions={iabCategoryOptions}
            />
          }
        />
        <Route path="*" element={<Navigate to={`../${creativeTabs.DETAILS}`} />} />
      </Routes>
    </PageHeader>
  );
};
